import React, { Fragment } from "react";
import PropTypes from "prop-types";
import Callout from "components/Callout";
import { useFormikContext } from "formik";
import { SCHEDULE_TYPE } from "utils/constants/price";
import isEmpty from "lodash/isEmpty";
import { getFieldPath, getFormCrudMode } from "utils/form";
import get from "lodash/get";
import ScheduleField from "components/ScheduleField";
import BillingPeriodField from "components/BillingPeriodField";
import AmountField from "components/AmountField";
import { FORM_MODE } from "utils/constants/form";
import ActiveField from "components/ActiveField";
import { UI_THEME, VIEW_ON_STRIPE } from "utils/constants/ui";
import TextareaController from "components/TextareaController";
import CopyCode from "components/CopyCode";
import StripeExternalLink from "components/StripeExternalLink";
import { useCtxReferences } from "utils/selectors";
import { getAccountConnectStatusProps } from "utils/merchantAccount";
import { getStripeDestination } from "utils/stripe";
import SectionBezel from "components/SectionBezel";
import MetadataSection from "components/MetadataSection";
import SegmentedControl from "components/SegmentedControl";
import { TAX_BEHAVIOR_OPTIONS } from "utils/constants/taxRate";
import { useStripeFeature } from "utils/hooks/stripe";
import { STRIPE_FEATURES } from "utils/constants/stripe";
import { hasSpecifiedTaxBehavior } from "utils/taxRate";

const PriceFormSection = ({ pathPrefix = "" }) => {
  const { merchantAccount } = useCtxReferences();
  const account =
    merchantAccount && getAccountConnectStatusProps(merchantAccount);

  const {
    values,
    touched,
    setFieldTouched,
    setFieldValue,
    initialValues
  } = useFormikContext();

  const formattedPricePath = getFieldPath(pathPrefix, `formattedPrice`);
  const recurringPath = getFieldPath(pathPrefix, `recurring`);
  const recurringIntervalPath = getFieldPath(pathPrefix, `recurring.interval`);
  const recurringIntervalCountPath = getFieldPath(
    pathPrefix,
    `recurring.interval_count`
  );
  const currencyPath = getFieldPath(pathPrefix, `currency`);
  const activePath = getFieldPath(pathPrefix, `active`);
  const idPath = getFieldPath(pathPrefix, `id`);
  const metadataPath = getFieldPath(pathPrefix, `metadata`);
  const nicknamePath = getFieldPath(pathPrefix, `nickname`);
  const taxBehaviorPath = getFieldPath(pathPrefix, `tax_behavior`);

  const taxEnabled = useStripeFeature({ feature: STRIPE_FEATURES.TAX });

  const formattedPrice = get(values, formattedPricePath);
  const recurringInterval = get(values, recurringIntervalPath);
  const currency = get(values, currencyPath);
  const active = get(values, activePath);
  const idValue = get(values, idPath);
  const taxBehavior = get(values, taxBehaviorPath);
  const initialTaxBehavior = get(initialValues, taxBehaviorPath);
  const taxBehaviorTouched = get(touched, taxBehaviorPath);

  const formMode = getFormCrudMode(idValue);
  const EDIT_MODE = formMode === FORM_MODE.EDIT;

  const scheduleValue = isEmpty(recurringInterval)
    ? SCHEDULE_TYPE.ONE_TIME
    : SCHEDULE_TYPE.RECURRING;
  let fields = [];
  if (EDIT_MODE) {
    fields.push(<ActiveField name={activePath} value={active} />);
  }

  fields.push(
    <AmountField
      disabled={EDIT_MODE}
      withCalculator={!EDIT_MODE}
      value={formattedPrice}
      name={formattedPricePath}
      currency={currency}
      currencyPath={currencyPath}
      recurringIntervalPath={recurringIntervalPath}
      recurringInterval={recurringInterval}
    />
  );

  fields.push(
    <ScheduleField
      name={recurringPath}
      intervalName={recurringIntervalPath}
      value={scheduleValue}
      disabled={EDIT_MODE}
    />
  );

  if (!isEmpty(recurringInterval)) {
    fields.push(
      <BillingPeriodField
        disabled={EDIT_MODE}
        intervalName={recurringIntervalPath}
        intervalCountName={recurringIntervalCountPath}
        value={recurringInterval}
      />
    );
  }

  if (taxEnabled) {
    const cantUpdateSpecifiedTaxBehavior =
      EDIT_MODE && hasSpecifiedTaxBehavior(initialTaxBehavior);
    const taxBehaviorProps = {
      options: TAX_BEHAVIOR_OPTIONS,
      theme: UI_THEME.SLIM,
      value: taxBehavior,
      disabled: cantUpdateSpecifiedTaxBehavior,
      onClick: (_, selection) => {
        if (!taxBehaviorTouched) {
          setFieldTouched(taxBehaviorPath, true);
        }
        setFieldValue(taxBehaviorPath, selection);
      }
    };

    fields.push(
      <Fragment>
        <div className="f6 mb2 dib fw6">Tax behavior</div>
        <SegmentedControl {...taxBehaviorProps} />
      </Fragment>
    );
  }

  fields.push(
    <TextareaController
      name={"nickname"}
      label={"Nickname"}
      key={nicknamePath}
      rootPath={pathPrefix}
      fieldKey={"nickname"}
      minRows={1}
    />
  );

  const actionComp = (
    <div className="flex flex-row items-center">
      <CopyCode
        label={""}
        value={values.id}
        customClasses={{
          value:
            "token-monospace f6 lh-copy word-break-all pa2 bg-content--secondary ba br2 hairline-1 w-100"
        }}
        s
        tooltip="Copy product id"
        toast={"Copied product id to clipboard"}
      />
      <StripeExternalLink
        account={account}
        tooltip={VIEW_ON_STRIPE}
        copy={` `}
        href={getStripeDestination({ path: `prices/${values.id}` })}
      />
    </div>
  );

  return (
    <Fragment>
      <SectionBezel header="Details" action={actionComp}>
        {fields.map((node, nodeIx) => (
          <div key={nodeIx} className={nodeIx ? "pt3 mt2" : ""}>
            {node}
          </div>
        ))}
      </SectionBezel>
      <div className="flex flex-column pt3">
        <Callout
          copy={`Note: Existing price amounts can't be edited, but you can create a new price.`}
          customClasses={{
            container:
              "flex pa2 ba br2 items-center hairline-1 mv2 mv0-ns bg-content--secondary",
            copy: "f7 lh-copy pr2 w-100"
          }}
        />
      </div>
      <div className="pt3">
        <MetadataSection header="Metadata" name={metadataPath} />
      </div>
    </Fragment>
  );
};

PriceFormSection.propTypes = {
  name: PropTypes.string,
  pathPrefix: PropTypes.string,
  product: PropTypes.object
};

export default PriceFormSection;
